import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router";
import type { BuyingGroupMode } from "./api/use-account-activities";
import { useTrackedBrowserStorage } from "./use-tracked-browser-storage";

// Interface for a custom buying group
export interface CustomBuyingGroup {
  id: string;
  name: string;
  opportunityIds: string[];
  personIds: string[];
  createdAt: string;
}

interface BuyingGroupSelectionContextType {
  // State directly from localStorage (or defaults)
  selectedOpportunityIds: string[];
  selectedPersonIds: string[];
  isCustomMode: boolean;
  customGroups: CustomBuyingGroup[];
  activeCustomGroupId: string | null;

  // Derived state remains the same
  buyingGroupMode: BuyingGroupMode;
  activeCustomGroupOpportunityIds: string[]; // Opp IDs for the active custom group
  activeCustomGroupPersonIds: string[]; // Person IDs for the active custom group

  // Simplified actions
  selectOpportunities: (opportunityIds: string[]) => void;
  selectCustomGroup: (groupId: string) => void;
  selectAll: () => void; // Replaces resetSelection conceptually for selecting "All"
  saveOrUpdateCustomGroup: (
    details: Pick<CustomBuyingGroup, "name" | "opportunityIds" | "personIds">,
    groupIdToUpdate?: string,
  ) => CustomBuyingGroup;
  deleteAndResetCustomGroup: (groupId: string) => void;
}

const BuyingGroupSelectionContext = createContext<
  BuyingGroupSelectionContextType | undefined
>(undefined);

// Single localStorage key for all buying group related data
const BUYING_GROUP_SETTINGS_KEY = "accounts::explorer::custom-buying-groups";

// Key names for settings within the nested structure
const CUSTOM_GROUPS_KEY = "customGroups";
const ACTIVE_GROUP_KEY = "activeGroupId";
const CUSTOM_MODE_KEY = "customMode";
const SELECTED_PERSONS_KEY = "selectedPersonIds";

export interface BuyingGroupSelectionProviderProps {
  children: ReactNode;
  accountId?: string; // Add accountId parameter to scope custom groups
}

// Interface for settings storage within a specific account
interface AccountSettings {
  [CUSTOM_GROUPS_KEY]?: CustomBuyingGroup[];
  [ACTIVE_GROUP_KEY]?: string | null;
  [CUSTOM_MODE_KEY]?: boolean;
  [SELECTED_PERSONS_KEY]?: string[];
  [key: string]: unknown; // Allow for future settings
}

// Interface for the simplified structure - just account settings by account ID
interface BuyingGroupSettings {
  [accountId: string]: AccountSettings;
}

export function BuyingGroupSelectionProvider({
  children,
  accountId,
}: BuyingGroupSelectionProviderProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const hasInitialized = useRef(false);

  // --- Local State for Opportunity IDs ---
  const [selectedOpportunityIds, setSelectedOpportunityIds] = useState<
    string[]
  >([]);

  // --- LocalStorage State (excluding Opp IDs) ---
  const [allSettings, setAllSettings] =
    useTrackedBrowserStorage<BuyingGroupSettings>(
      BUYING_GROUP_SETTINGS_KEY,
      {},
      {
        storage: "localStorage",
        migration: {
          from: {
            key: "customBuyingGroupSettings",
            storage: "localStorage",
            organizationScoped: true,
          },
          cleanup: true,
        },
      },
    );

  // --- Derive current account settings from localStorage (excluding Opp IDs) ---
  const accountSettings = useMemo(() => {
    if (!accountId) {
      return {
        [CUSTOM_GROUPS_KEY]: [],
        [ACTIVE_GROUP_KEY]: null,
        [CUSTOM_MODE_KEY]: false,
        [SELECTED_PERSONS_KEY]: [],
      } as AccountSettings;
    }
    const currentAccountSettings = allSettings[accountId] || {};
    return {
      [CUSTOM_GROUPS_KEY]: currentAccountSettings[CUSTOM_GROUPS_KEY] || [],
      [ACTIVE_GROUP_KEY]: currentAccountSettings[ACTIVE_GROUP_KEY] || null,
      [CUSTOM_MODE_KEY]: currentAccountSettings[CUSTOM_MODE_KEY] || false,
      [SELECTED_PERSONS_KEY]:
        currentAccountSettings[SELECTED_PERSONS_KEY] || [],
    } as AccountSettings;
  }, [allSettings, accountId]);

  // Extract localStorage-backed state values
  const customGroups = accountSettings[
    CUSTOM_GROUPS_KEY
  ] as CustomBuyingGroup[];
  const activeCustomGroupId = accountSettings[ACTIVE_GROUP_KEY] as
    | string
    | null;
  const isCustomMode = accountSettings[CUSTOM_MODE_KEY] as boolean;
  // selectedPersonIds are primarily relevant when isCustomMode is true
  const selectedPersonIds = accountSettings[SELECTED_PERSONS_KEY] as string[];

  // --- Helper for atomic localStorage updates (excluding Opp IDs) ---
  const updateAccountSettings = useCallback(
    (newLocalStorageSettings: Partial<AccountSettings>) => {
      if (!accountId) return;

      setAllSettings((prevAllSettings) => {
        const currentAllSettings = prevAllSettings || {};
        const currentSettings = currentAllSettings[accountId] || {};

        return {
          ...currentAllSettings,
          [accountId]: { ...currentSettings, ...newLocalStorageSettings },
        };
      });
    },
    [accountId, setAllSettings],
  );

  // --- Action Functions ---
  // Define actions before the initialization effect that might call them

  const selectOpportunities = useCallback(
    (opportunityIds: string[]) => {
      if (!accountId) return;

      // 1. Set local state
      setSelectedOpportunityIds(opportunityIds);

      // 2. Update localStorage (Mode=Opp, No Active Group, Clear Persons)
      updateAccountSettings({
        [CUSTOM_MODE_KEY]: false,
        [ACTIVE_GROUP_KEY]: null,
        [SELECTED_PERSONS_KEY]: [], // Clear persons when entering opp mode
      });

      // 3. Update URL
      const newParams = new URLSearchParams(searchParams);
      if (opportunityIds.length > 0) {
        newParams.set("opportunityId", opportunityIds.join(","));
      } else {
        newParams.delete("opportunityId");
      }
      setSearchParams(newParams, { replace: true });
    },
    [accountId, updateAccountSettings, searchParams, setSearchParams],
  );

  const selectCustomGroup = useCallback(
    (groupId: string) => {
      if (!accountId) return;
      const groupToSelect = customGroups.find((g) => g.id === groupId);

      if (groupToSelect) {
        // 1. Clear local Opp ID state
        setSelectedOpportunityIds([]);

        // 2. Update localStorage (Mode=Custom, Set Active Group, Set Persons)
        updateAccountSettings({
          [CUSTOM_MODE_KEY]: true,
          [ACTIVE_GROUP_KEY]: groupId,
          [SELECTED_PERSONS_KEY]: groupToSelect.personIds,
        });

        // 3. Update URL (Remove param)
        const newParams = new URLSearchParams(searchParams);
        newParams.delete("opportunityId");
        setSearchParams(newParams, { replace: true });
      } else {
        console.warn(`Custom group with ID ${groupId} not found.`);
      }
    },
    [
      accountId,
      customGroups,
      updateAccountSettings,
      searchParams,
      setSearchParams,
    ],
  );

  const selectAll = useCallback(() => {
    if (!accountId) return;

    // 1. Clear local Opp ID state
    setSelectedOpportunityIds([]);

    // 2. Update localStorage (Mode=Default, No Active Group, Clear Persons)
    updateAccountSettings({
      [CUSTOM_MODE_KEY]: false,
      [ACTIVE_GROUP_KEY]: null,
      [SELECTED_PERSONS_KEY]: [],
    });

    // 3. Update URL (Remove param)
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("opportunityId");
    setSearchParams(newParams, { replace: true });
  }, [accountId, updateAccountSettings, searchParams, setSearchParams]);

  const saveOrUpdateCustomGroup = useCallback(
    (
      details: Pick<CustomBuyingGroup, "name" | "opportunityIds" | "personIds">,
      groupIdToUpdate?: string,
    ): CustomBuyingGroup => {
      if (!accountId) {
        throw new Error("Account ID missing during save/update.");
      }

      // Logic to get updatedGroups and savedGroup (remains same)
      const currentGroups = customGroups;
      let savedGroup: CustomBuyingGroup | undefined;
      let updatedGroups: CustomBuyingGroup[];
      if (groupIdToUpdate) {
        /* ... update logic ... */
        let groupFound = false;
        updatedGroups = currentGroups.map((group) => {
          if (group.id === groupIdToUpdate) {
            groupFound = true;
            savedGroup = { ...group, ...details };
            return savedGroup;
          }
          return group;
        });
        if (!groupFound || !savedGroup) {
          throw new Error(`Group ${groupIdToUpdate} not found`);
        }
      } else {
        /* ... create logic ... */
        savedGroup = {
          id: Date.now().toString(),
          createdAt: new Date().toISOString(),
          ...details,
        };
        updatedGroups = [...currentGroups, savedGroup];
      }

      // 1. Clear local Opp ID state
      setSelectedOpportunityIds([]);

      // 2. Update localStorage (Save Groups, Mode=Custom, Set Active Group, Set Persons)
      updateAccountSettings({
        [CUSTOM_GROUPS_KEY]: updatedGroups,
        [ACTIVE_GROUP_KEY]: savedGroup.id,
        [CUSTOM_MODE_KEY]: true,
        [SELECTED_PERSONS_KEY]: savedGroup.personIds,
      });

      // 3. Update URL (Remove param)
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("opportunityId");
      setSearchParams(newParams, { replace: true });

      return savedGroup;
    },
    [
      accountId,
      customGroups,
      updateAccountSettings,
      searchParams,
      setSearchParams,
    ],
  );

  const deleteAndResetCustomGroup = useCallback(
    (groupId: string) => {
      if (!accountId) return;

      const updatedGroups = customGroups.filter(
        (group) => group.id !== groupId,
      );
      let localStorageUpdate: Partial<AccountSettings> = {
        [CUSTOM_GROUPS_KEY]: updatedGroups,
      };
      let shouldClearUrlAndLocalState = false;

      // If the deleted group was the active one, reset to "All"
      if (activeCustomGroupId === groupId) {
        localStorageUpdate = {
          ...localStorageUpdate,
          [CUSTOM_MODE_KEY]: false,
          [ACTIVE_GROUP_KEY]: null,
          [SELECTED_PERSONS_KEY]: [],
        };
        shouldClearUrlAndLocalState = true;
      }

      // 1. Update localStorage
      updateAccountSettings(localStorageUpdate);

      // 2. & 3. Clear local state and URL if needed
      if (shouldClearUrlAndLocalState) {
        setSelectedOpportunityIds([]);
        const newParams = new URLSearchParams(searchParams);
        newParams.delete("opportunityId");
        setSearchParams(newParams, { replace: true });
      }
    },
    [
      accountId,
      customGroups,
      activeCustomGroupId,
      updateAccountSettings,
      searchParams,
      setSearchParams,
    ],
  );

  // --- Revised Initialization Logic ---
  useEffect(() => {
    // Ensure this runs only once per accountId combination
    if (!hasInitialized.current && accountId) {
      const urlOppIdParam = searchParams.get("opportunityId");

      if (urlOppIdParam) {
        // URL Parameter takes precedence
        const idsFromUrl = urlOppIdParam.split(",").filter(Boolean);
        if (idsFromUrl.length > 0) {
          // Directly set local state and update localStorage for mode
          setSelectedOpportunityIds(idsFromUrl);
          updateAccountSettings({
            [CUSTOM_MODE_KEY]: false,
            [ACTIVE_GROUP_KEY]: null,
            [SELECTED_PERSONS_KEY]: [],
          });
          // No need to call selectOpportunities as it would re-set the URL
        } else {
          // Invalid/empty URL param, treat as "All"
          selectAll();
        }
      } else {
        // No URL param, check localStorage state
        const storedState = allSettings[accountId];
        if (storedState?.[CUSTOM_MODE_KEY]) {
          // Already in custom mode in localStorage, ensure local state is clear
          setSelectedOpportunityIds([]);
          // Active group and persons are already loaded via accountSettings derivation
        } else {
          // Not custom mode in localStorage, ensure "All" state
          selectAll();
        }
      }
      hasInitialized.current = true; // Mark as initialized
    }
    // Reset initialization flag if accountId changes
    const currentAccountId = accountId;
    return () => {
      if (currentAccountId !== accountId) {
        hasInitialized.current = false;
      }
    };
  }, [accountId, searchParams, allSettings, updateAccountSettings, selectAll]);

  // --- Derived State ---
  const buyingGroupMode: BuyingGroupMode = useMemo(() => {
    if (isCustomMode) return "custom"; // isCustomMode is from localStorage
    if (selectedOpportunityIds.length > 0) return "opportunity"; // selectedOpportunityIds is local state
    return "default";
  }, [isCustomMode, selectedOpportunityIds]);

  const activeCustomGroup = useMemo(() => {
    if (!isCustomMode || !activeCustomGroupId) return null;
    return (
      customGroups.find((group) => group.id === activeCustomGroupId) ?? null
    );
  }, [isCustomMode, activeCustomGroupId, customGroups]);

  const activeCustomGroupOpportunityIds = useMemo(
    () => activeCustomGroup?.opportunityIds ?? [],
    [activeCustomGroup],
  );
  const activeCustomGroupPersonIds = useMemo(
    () => activeCustomGroup?.personIds ?? [],
    [activeCustomGroup],
  ); // Derive from activeCustomGroup.personIds for consistency

  // --- Context Provider Value ---
  const contextValue = useMemo(
    () => ({
      selectedOpportunityIds,
      selectedPersonIds,
      isCustomMode,
      customGroups,
      activeCustomGroupId,
      buyingGroupMode,
      activeCustomGroupOpportunityIds,
      activeCustomGroupPersonIds,
      selectOpportunities,
      selectCustomGroup,
      selectAll,
      saveOrUpdateCustomGroup,
      deleteAndResetCustomGroup,
    }),
    [
      selectedOpportunityIds,
      selectedPersonIds,
      isCustomMode,
      customGroups,
      activeCustomGroupId,
      buyingGroupMode,
      activeCustomGroupOpportunityIds,
      activeCustomGroupPersonIds,
      selectOpportunities,
      selectCustomGroup,
      selectAll,
      saveOrUpdateCustomGroup,
      deleteAndResetCustomGroup,
    ],
  );

  return (
    <BuyingGroupSelectionContext.Provider value={contextValue}>
      {children}
    </BuyingGroupSelectionContext.Provider>
  );
}

export function useBuyingGroupSelection() {
  const context = useContext(BuyingGroupSelectionContext);
  if (context === undefined) {
    throw new Error(
      "useBuyingGroupSelection must be used within a BuyingGroupSelectionProvider",
    );
  }
  return context;
}
